import React, { ReactElement } from 'react'
import { useFormikContext } from 'formik'
import { useShopCountry } from '@cbgms/base/shop-country'
import { CountryCode } from '@carsys/enums/country-code'
import { Locales } from '@carsys/enums/locales'
import { IVehicleType, VehicleProvider } from '@cbgms/api'
import { ICreateUnknownVehicleForm } from 'app/vehicles/components/UnknownVehicleV2/hooks/createVehicleRequestForUnknownVehicle'
import { ISetVehicleOptions, UnknownVehicleV2ForTree } from 'app/vehicles/helpers/VehicleTreeUknownVehiclev2'
import { usePrimaryVehicleSource } from 'app/vehicles/helpers/use-primary-vehicle-source'
import { useDefaultOrderAccountVehicleProvider } from 'app/vehicles/helpers/use-default-order-account-vehicle-provider'

const VPOMProviderToComponent: Record<string, React.ComponentType<IProviderSelectionProps> | undefined> = {
  [VehicleProvider.Hbase]: UnknownVehicleV2ForTree,
  [VehicleProvider.TecDoc]: UnknownVehicleV2ForTree,
  [VehicleProvider.TecDocPlus]: UnknownVehicleV2ForTree,
  [VehicleProvider.Haynes]: UnknownVehicleV2ForTree,
  [VehicleProvider.Mdm]: UnknownVehicleV2ForTree
}

const ProviderToComponent: Record<string, React.ComponentType<IProviderSelectionProps> | undefined> = {
  [VehicleProvider.Hbase]: UnknownVehicleV2ForTree,
  [VehicleProvider.TecDoc]: UnknownVehicleV2ForTree,
  [VehicleProvider.TecDocPlus]: UnknownVehicleV2ForTree,
  [VehicleProvider.Haynes]: UnknownVehicleV2ForTree,
  [VehicleProvider.Mdm]: UnknownVehicleV2ForTree
}

export const useCountryResolverForProviders = () => {
  const shopCountry = useShopCountry()
  const {
    values: { Country: selectedCountry }
  } = useFormikContext<ICreateUnknownVehicleForm>()
  return (selectedCountry || shopCountry?.country || 'NL') as CountryCode
}

/**
 * Looks up the vehicle tree selection component (MMTSelection/MDMSelection) for the specified country.
 */
export const useVehicleTreeProviderForProvider = (provider?: VehicleProvider): React.ComponentType<IProviderSelectionProps> | undefined => {
  const { isVPOM } = usePrimaryVehicleSource()
  if (provider) {
    if (isVPOM) {
      return VPOMProviderToComponent[provider]
    }
    return ProviderToComponent[provider]
  }
  return undefined
}

export const useVehicleTreeProviderForDefaultOrderAccount = () => {
  const provider = useDefaultOrderAccountVehicleProvider()
  const Component = useVehicleTreeProviderForProvider(provider) as React.ComponentType<IProviderSelectionProps>
  return { Component, provider }
}

interface IProviderSelectionProps {
  actionButton?: ReactElement | null | undefined
  onTypeSelection?: () => Promise<void>
  onSelection: (vehicle: IVehicleType | {}, options?: ISetVehicleOptions) => void
  staticLicensePlate?: string
  vehicle: IVehicleType | {}
  country: CountryCode
  typeSelectMode?: boolean
  locale: Locales
  provider: VehicleProvider
  headerContent: React.ReactNode // headerContent allows you to pass additional fields/things to render next to selection. IE a vin input
}
